import {
    DefaultContentAcquisitionEntities,
    DefaultContentAcquisitionEntity,
    RatchetModels,
    Region,
    Regions,
    Service,
    ServiceLaunches,
    Services,
    Territories,
    Territory
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { sortBy } from 'lodash';
import { ALL_SERVICE, CM_UI_SUPPORT_SERVICE_ID_LIST, GLOBAL_TERRITORY } from 'src/constants/context';

/**
 * Builds a map of services from API response
 * @param services - Array of services from API response
 * @returns Map from service ID to service object, including an "All Services" entry
 */
export const buildServiceMap = (services: Services): Record<number, Service> => {
    const serviceObj =
        services.reduce(
            (accu, curr) => {
                if (curr.serviceId && CM_UI_SUPPORT_SERVICE_ID_LIST.includes(curr.serviceId)) {
                    accu[curr.serviceId] = curr;
                }
                return accu;
            },
            {} as Record<number, Service>
        ) ?? ({} as Record<number, Service>);

    if (serviceObj) {
        serviceObj['0'] = { serviceId: ALL_SERVICE.ID, serviceName: ALL_SERVICE.NAME };
    }

    return serviceObj;
};

/**
 * Builds a map of territories from API response
 * @param territories - Array of territories from API response
 * @returns Map from territory ID to territory object, including a global territory entry
 */
export const buildTerritoryMap = (territories: Territories): Record<number, Territory> => {
    const territoryObj = territories.reduce(
        (accu, curr) => {
            if (curr.territoryId) {
                accu[curr.territoryId] = curr;
            }
            return accu;
        },
        {} as Record<number, Territory>
    );

    if (territoryObj) {
        territoryObj['0'] = {
            territoryId: GLOBAL_TERRITORY.ID,
            territoryCode: GLOBAL_TERRITORY.NAME,
            territoryName: GLOBAL_TERRITORY.NAME,
            currency: ''
        };
    }

    return territoryObj;
};

/**
 * Builds a map of regions from API response
 * @param regions - Array of regions from API response
 * @returns Map from region ID to region object
 */
export const buildRegionMap = (regions: Regions): Record<number, Region> =>
    regions.reduce(
        (accu, curr) => {
            if (curr.regionId) {
                accu[curr.regionId] = curr;
            }
            return accu;
        },
        {} as Record<number, Region>
    );

/**
 * Builds a map from getServiceLaunchDate API
 * @param serviceLaunchDates service launches from API response
 * @returns Map from service ID to launches
 */
export const buildServiceLaunchDateMap = (serviceLaunchDates: ServiceLaunches): Record<string, ServiceLaunches> => {
    const map: Record<string, ServiceLaunches> = {};

    for (const serviceLaunchDate of serviceLaunchDates) {
        if (!map[serviceLaunchDate.serviceID!]) {
            map[serviceLaunchDate.serviceID!] = [];
        }

        const startDate = serviceLaunchDate.launchDate;
        const endDate = serviceLaunchDate.endDate;

        if (endDate) {
            if (endDate >= new Date()) {
                map[serviceLaunchDate.serviceID!].push(serviceLaunchDate);
            }
        } else {
            if (startDate && startDate <= new Date()) {
                map[serviceLaunchDate.serviceID!].push(serviceLaunchDate);
            }
        }
    }

    Object.keys(map).forEach((serviceID) => {
        map[serviceID] = sortBy(map[serviceID], 'territoryCode');
    });

    return map;
};

/**
 * Builds a map from getDefaultContentAcquisitionEntity API
 * @param defaultCaes default CAEs from API response
 * @returns Map from CAE ID to default CAE
 */
export const buildDefaultCaeMap = (
    defaultCaes: DefaultContentAcquisitionEntities
): Record<number, DefaultContentAcquisitionEntity> =>
    defaultCaes.reduce(
        (accu, curr) => {
            if (curr.defaultContentAcquisitionEntityID) {
                accu[curr.defaultContentAcquisitionEntityID] = curr;
            }

            return accu;
        },
        {} as Record<number, DefaultContentAcquisitionEntity>
    );

/**
 * Builds a map of ratchet models from API response
 * @param ratchetModels - Array of ratchet models from API response
 * @returns Map from ratchet model name to ratchet model ID
 */
export const buildRatchetModelMap = (ratchetModels: RatchetModels): Record<string, number> => {
    const ratchetModelMapping: Record<string, number> = {};
    ratchetModels.forEach((model) => (ratchetModelMapping[model.ratchetModel!] = model.ratchetModelId!));

    return ratchetModelMapping;
};
